import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useProperty } from '@/lib/property/propertyContextDef';
import { useBookingsList, guestName, listingTitle, LIST_LIMIT, type BookingFilters } from '@/lib/data/bookings';
import { useBookingsRealtime } from '@/lib/data/bookingsRealtime';
import { formatNad } from '@/lib/money/format';
import { computeBookingMoney } from '@/lib/money/bookingMoney';
import { StatusPill, FlagBadges } from '@/lib/bookings/StatusPill';
import { downloadCsv, toCsv, type CsvColumn } from '@/lib/csv/toCsv';
import { DownloadIcon, SearchIcon, CalendarIcon, AlertIcon } from '@/icons';
import type { BookingDisplayStatus } from '@namibiago/shared/policy';
import type { BookingRow } from '@/lib/data/bookings';
import { SkeletonRows } from '@/ui/Loader';
import { ButtonLink, Button, EmptyState, Field, Notice, PageHeader } from '@/ui/primitives';
import { inputClass } from '@/ui/classes';

export const Route = createFileRoute('/bookings/')({
  component: BookingsList,
});

const STATUS_OPTIONS: BookingDisplayStatus[] = [
  'Pending',
  'Confirmed',
  'Checked in',
  'Fully paid',
  'Balance outstanding',
  'Balance not recorded',
  'Completed',
  'No-show',
  'Cancelled·host',
  'Cancelled·guest',
  'Expired',
];

const CSV_COLUMNS: CsvColumn<BookingRow>[] = [
  { header: 'Reference', value: (r) => r.reference_number ?? r.id },
  { header: 'Guest', value: (r) => guestName(r) },
  { header: 'Listing', value: (r) => listingTitle(r) },
  { header: 'Check-in', value: (r) => r.check_in },
  { header: 'Check-out', value: (r) => r.check_out },
  { header: 'Guests', value: (r) => r.guests },
  { header: 'Status', value: (r) => r.display_status ?? r.status },
  {
    header: 'Total (N$)',
    value: (r) => formatNad(r.total_price).replace('N$', ''),
  },
  {
    // The in-app charge: the fee, or the whole price for a full charge (C1).
    header: 'Paid in-app (N$)',
    value: (r) =>
      formatNad(
        computeBookingMoney({
          totalPrice: r.total_price,
          depositAmount: r.deposit_amount,
          balanceRecordedAmountNad: r.balance_recorded_amount_nad,
          feeStatus: r.fee_status,
          balanceStatus: r.balance_status,
        }).feePaidCents / 100,
      ).replace('N$', ''),
  },
  {
    header: 'Balance due (N$)',
    value: (r) =>
      formatNad(
        computeBookingMoney({
          totalPrice: r.total_price,
          depositAmount: r.deposit_amount,
          balanceRecordedAmountNad: r.balance_recorded_amount_nad,
          feeStatus: r.fee_status,
          balanceStatus: r.balance_status,
        }).balanceDueCents / 100,
      ).replace('N$', ''),
  },
];

function BookingsList() {
  const { businessId } = useProperty();
  useBookingsRealtime(businessId);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BookingDisplayStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters: BookingFilters = useMemo(
    () => ({
      search: search || undefined,
      statuses: statusFilter ? [statusFilter] : undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [search, statusFilter, from, to],
  );

  const { data: result, isLoading, error } = useBookingsList(businessId, filters);
  const bookings = result?.rows;

  function handleExport() {
    if (!bookings) return;
    const csv = toCsv(CSV_COLUMNS, bookings);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`bookings-${stamp}.csv`, csv);
  }

  return (
    <section className="flex flex-col gap-lg">
      <PageHeader
        title="Bookings"
        actions={
          <>
            <ButtonLink to="/bookings/calendar" variant="secondary" icon={<CalendarIcon className="h-4 w-4" aria-hidden="true" />}>
              Calendar
            </ButtonLink>
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={!bookings || bookings.length === 0}
              icon={<DownloadIcon className="h-4 w-4" aria-hidden="true" />}
            >
              Export CSV
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-md rounded-card border border-border bg-surface p-md shadow-sm sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]">
        <Field label="Search">
          <span className="relative block">
            <SearchIcon className="pointer-events-none absolute left-md top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" aria-hidden="true" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Reference or guest name"
              className={`${inputClass} pl-[38px]`}
              aria-label="Search bookings by reference or guest name"
            />
          </span>
        </Field>
        <Field label="Status">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as BookingDisplayStatus | '')}
            className={inputClass}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Check-in from">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Check-in to">
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} />
        </Field>
      </div>

      {isLoading ? <SkeletonRows rows={6} label="Loading bookings…" /> : null}
      {error ? (
        <Notice tone="danger" role="alert">
          Couldn't load bookings: {(error as Error).message}
        </Notice>
      ) : null}
      {result?.isLimited ? (
        <Notice tone="warning">
          <AlertIcon className="mr-xs inline h-4 w-4 align-[-3px] text-warning" aria-hidden="true" />
          Showing the first {LIST_LIMIT} — narrow your filters (check-in dates) to see more.
        </Notice>
      ) : null}

      {!isLoading && !error ? (
        bookings && bookings.length > 0 ? (
          <div className="overflow-x-auto rounded-card border border-border bg-surface shadow-sm">
            <table className="w-full min-w-[720px] text-body text-ink">
              <thead>
                <tr className="border-b border-hair bg-surface-2 text-left text-small text-ink-2">
                  <th className="px-md py-sm font-semibold">Reference</th>
                  <th className="px-md py-sm font-semibold">Guest</th>
                  <th className="px-md py-sm font-semibold">Dates</th>
                  <th className="px-md py-sm font-semibold">Status</th>
                  <th className="px-md py-sm text-right font-semibold">Balance due</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => {
                  const money = computeBookingMoney({
                    totalPrice: b.total_price,
                    depositAmount: b.deposit_amount,
                    balanceRecordedAmountNad: b.balance_recorded_amount_nad,
                    feeStatus: b.fee_status,
                    balanceStatus: b.balance_status,
                  });
                  return (
                    <tr key={b.id} className="border-b border-hair last:border-0 hover:bg-surface-2">
                      <td className="px-md py-sm">
                        <Link
                          to="/bookings/$reference"
                          params={{ reference: b.reference_number ?? b.id }}
                          className="font-mono text-small font-semibold text-canyon hover:underline"
                        >
                          {b.reference_number ?? b.id}
                        </Link>
                      </td>
                      <td className="px-md py-sm">{guestName(b)}</td>
                      <td className="px-md py-sm text-ink-2">
                        {b.check_in ?? '—'} → {b.check_out ?? '—'}
                      </td>
                      <td className="px-md py-sm">
                        <div className="flex flex-col items-start gap-xs">
                          <StatusPill status={b.display_status} />
                          <FlagBadges flags={b.display_flags} />
                        </div>
                      </td>
                      <td className={`px-md py-sm text-right font-semibold tabular-nums ${money.balanceDueCents > 0 ? 'text-canyon' : 'text-ink-2'}`}>
                        {formatNad(money.balanceDueCents / 100)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<SearchIcon />}
            title="No bookings match these filters"
            body="Widen the check-in dates or clear the status filter. New bookings appear here the moment a guest pays."
          />
        )
      ) : null}
    </section>
  );
}
